import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play } from "lucide-react";
import { IMAGES, SOCIALS, LATEST_SINGLE } from "../../data/velvet";
import { MaskLine, FadeUp, Overline } from "./Reveal";

const META = [
  ["Status", LATEST_SINGLE.status],
  ["Featuring", LATEST_SINGLE.feat],
  ["From the album", LATEST_SINGLE.album],
];

export const Release = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section
      id="release"
      data-testid="release-section"
      ref={ref}
      className="relative overflow-hidden border-t border-vt-gold/10 bg-vt-surface py-28 md:py-40"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-16 lg:px-24">
        <div className="mb-16 flex items-center gap-4 md:mb-24">
          <Overline>Latest Release</Overline>
          <div className="vt-hairline flex-1" />
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-vt-muted">Debut Single</span>
        </div>

        <div className="grid grid-cols-1 items-center gap-16 lg:grid-cols-2 lg:gap-24">
          {/* Cover + vinyl */}
          <FadeUp className="relative mx-auto w-full max-w-md lg:mx-0">
            <div className="relative">
              {/* Vinyl peeking out */}
              <motion.div
                style={{ rotate }}
                className="absolute -right-10 top-1/2 z-0 h-[85%] w-[85%] -translate-y-1/2 md:-right-20"
              >
                <div className="vt-vinyl-spin h-full w-full rounded-full bg-[radial-gradient(circle,#1a1712_0%,#0a0a0a_38%,#1c1912_40%,#0a0a0a_46%,#1c1912_48%,#0a0a0a_60%,#141210_62%,#0a0a0a_100%)] shadow-[0_30px_80px_rgba(0,0,0,0.7)]">
                  <div className="absolute left-1/2 top-1/2 h-[26%] w-[26%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-vt-gold/90" />
                  <div className="absolute left-1/2 top-1/2 h-[4%] w-[4%] -translate-x-1/2 -translate-y-1/2 rounded-full bg-vt-black" />
                </div>
              </motion.div>
              {/* Cover */}
              <div className="relative z-10 overflow-hidden rounded-sm shadow-[0_40px_90px_rgba(0,0,0,0.6)]">
                <img
                  src={IMAGES.release}
                  alt="Album cover for When The House Goes Quiet by Ellis Marrow"
                  className="aspect-square w-full object-cover"
                />
                <div className="pointer-events-none absolute inset-0 border border-vt-gold/20" />
              </div>
            </div>
          </FadeUp>

          {/* Details */}
          <div>
            <Overline className="text-vt-gold/80">The Velvet Testament · feat. Ellis Marrow</Overline>
            <h2 className="mt-4 font-serif font-light leading-[0.95] tracking-tight text-vt-cream">
              <span className="block text-4xl sm:text-5xl md:text-6xl">
                <MaskLine>When The House</MaskLine>
              </span>
              <span className="block text-4xl italic text-vt-gold sm:text-5xl md:text-6xl">
                <MaskLine delay={0.08}>Goes Quiet</MaskLine>
              </span>
            </h2>

            <FadeUp delay={0.12}>
              <p className="mt-8 max-w-lg font-body text-base font-light leading-relaxed tracking-wide text-vt-ash md:text-lg">
                The debut single of The Velvet Testament — a warm, weathered
                baritone ballad about the rooms we leave behind and the people
                who once filled them. The first song from the album{" "}
                <span className="italic text-vt-cream">Rooms That Learned to Echo</span>.
              </p>
            </FadeUp>

            <FadeUp delay={0.18}>
              <dl className="mt-10 divide-y divide-vt-gold/10 border-y border-vt-gold/10">
                {META.map(([label, value]) => (
                  <div
                    key={label}
                    data-testid={`release-meta-${label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="flex items-baseline gap-5 py-3.5"
                  >
                    <dt className="w-40 shrink-0 font-mono text-xs uppercase tracking-[0.2em] text-vt-muted">
                      {label}
                    </dt>
                    <dd className="flex-1 font-serif text-xl tracking-tight text-vt-cream">
                      {value}
                    </dd>
                  </div>
                ))}
              </dl>
            </FadeUp>

            <FadeUp delay={0.24}>
              <div className="mt-9 flex flex-wrap gap-4">
                <a
                  data-testid="release-spotify-btn"
                  href={SOCIALS.spotify}
                  target="_blank"
                  rel="noreferrer"
                  className="group inline-flex items-center gap-3 rounded-full bg-vt-gold px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-vt-black transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-vt-cream"
                >
                  <Play size={15} fill="currentColor" />
                  Listen on Spotify
                </a>
              </div>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
};
